import { useCallback, useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { TheatreContext } from "../../components/site/SiteLayout";
import DelegateVerify from "../../components/virtual/DelegateVerify";
import RoomMeeting from "../../components/virtual/RoomMeeting";
import { api } from "../../lib/api";
import { useBrandImage } from "../../lib/brand";
import { formatEventDate } from "../../lib/format";
import { DELEGATE_KEY, delegateApi } from "../../lib/delegate";
import { consumeHandoff, useIsolationReload } from "../../lib/meeting";

function RoomHeader({ me, live }) {
  const logo = useBrandImage("logo");
  return (
    <header className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b-2 border-gold bg-black px-4 py-2.5 text-white sm:px-6" data-testid="room-header">
      <img src={logo.src} srcSet={logo.srcSet} sizes="120px" alt="CIArb Bahamas Branch" className="h-9 w-auto" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold" data-testid="room-event-name">{me?.event?.name || "Arbitration Day 2026"}</p>
        {me?.event && <p className="text-xs text-white/70" data-testid="room-event-date">{formatEventDate(me.event)}</p>}
      </div>
      <span className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${live ? "bg-burgundy" : "bg-white/10"}`} data-testid="room-live-badge">
        <span className={`h-2 w-2 rounded-full ${live ? "animate-pulse bg-white" : "bg-gold"}`} aria-hidden="true" />{live ? "Live" : "Event room"}</span>
    </header>
  );
}

const Panel = ({ children, testid }) => <div className="mx-auto my-10 max-w-xl rounded-lg bg-white p-6 text-navy shadow-xl sm:p-8" data-testid={testid}>{children}</div>;

export default function EventRoom() {
  const [theatre, setTheatre] = useState(false);
  const [live, setLive] = useState(false);
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [hasSession, setHasSession] = useState(() => { consumeHandoff(); return !!sessionStorage.getItem(DELEGATE_KEY); });
  useIsolationReload();
  useEffect(() => { document.title = "Event room · CIArb Bahamas"; api.get("/virtual/status").then((r) => setStatus(r.data)); }, []);
  const loadMe = useCallback(() => {
    const v = Number(sessionStorage.getItem("ciarb_t_verified"));
    if (v) console.info(`[event-room] verify-done -> room page ${Date.now() - v} ms`);
    const t0 = performance.now();
    delegateApi.get("/virtual/me").then((r) => { console.info(`[event-room] event lookup ${Math.round(performance.now() - t0)} ms (page +${Math.round(performance.now())} ms)`); setMe(r.data); })
      .catch(() => { sessionStorage.removeItem(DELEGATE_KEY); setHasSession(false); });
  }, []);
  useEffect(() => { if (hasSession) loadMe(); }, [hasSession, loadMe]);

  let body;
  if (!status || (hasSession && !me)) body = <p className="p-10 text-center text-white/80" aria-busy="true">Loading…</p>;
  else if (!hasSession) body = <Panel testid="room-verify"><DelegateVerify status={status} onVerified={() => setHasSession(true)} /></Panel>;
  else if (!me.eligible) body = (
    <Panel testid="room-ineligible"><p className="flex items-start gap-3" role="alert"><ShieldAlert className="shrink-0 text-burgundy" aria-hidden="true" />
      <span><b className="block">Virtual access not available</b>{me.message} <span className="text-xs text-[#4A5568]">(code: {me.reason})</span></span></p></Panel>);
  else body = <RoomMeeting me={me} onLive={setLive} />;

  return (
    <TheatreContext.Provider value={{ theatre, setTheatre }}>
      <div className="flex min-h-screen flex-col overflow-x-hidden bg-navy text-white" data-testid="event-room">
        {!theatre && <RoomHeader me={me} live={live} />}
        <main id="main" className="flex-1">{body}</main>
      </div>
    </TheatreContext.Provider>
  );
}
